import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";

import { SiteByIdDTO, deleteCollectorById, getSiteById, getUnitCostByCollectorId, updateCollector } from "api";
import { EsgDeleteIcon, EsgNotesIcon, EsgRecIcon, EsgTruck, PlusIcon } from "assets";
import { COLORS, DeleteDialog, SwitchToActivation, CollectorModal, CustomMaterialTable } from "components";
import { useDeActivatedCollectorsBySiteId, useNotify } from "hooks";
import { AllowedFormValues } from "types";
import { useUserRole } from "recoils";
import { QUERY_KEYS } from "consts";

import { collectorsColumns } from "./consts";
import { UnitCostInfoDrawer } from "./components";

export const UtilityProviders = ({
  siteData,
  site,
  allowedValues,
  onCollectorDataUpdate,
  setSiteData,
  isSuperAdmin,
}: {
  siteData: SiteByIdDTO,
  site: SiteByIdDTO,
  allowedValues: AllowedFormValues,
  onCollectorDataUpdate: (siteData: SiteByIdDTO) => void,
  setSiteData: React.Dispatch<React.SetStateAction<SiteByIdDTO | undefined>>,
  isSuperAdmin: boolean,
}) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const userRole = useUserRole();
  const queryClient = useQueryClient();

  const collectorRecords = JSON.parse(JSON.stringify(siteData?.collectors || []));

  const { data: DeActivatedCollectorsFromApi, refetch: refetchCollectors, isFetching: isDeActivatedCollectorsFetching } = useDeActivatedCollectorsBySiteId(siteData?.id ?? null);

  const { mutateAsync: updateCollectorRequest, isLoading: isUpdateCollectorPending } = useMutation(updateCollector);
  const { mutateAsync: deleteCollectorRequest, isLoading: isDeleteCollectorPending } = useMutation(deleteCollectorById);

  const [isSiteUpdating, setIsSiteUpdating] = useState<boolean>(false);
  const [isCollectorModalOpen, setCollectorModalOpen] = useState<boolean>(false);
  const [editCollector, setEditCollector] = useState<any | undefined>();
  const [clickType, setClickType] = useState<string>("");
  const [isNotesOnlyMode, setIsNotesOnlyMode] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [currentCollectorToDelete, setCurrentCollectorToDelete] = useState<any>(null);

  const [isEditUnitCost, setIsEditUnitCost] = useState<boolean>(false);
  const [showSideInfoDrawer, setShowSideInfoDrawer] = useState<boolean>(false);
  const [UnitCostData, setUnitCostData] = useState<any | undefined>();
  const [openSnackBar, setOpenSnackBar] = useState<boolean>(false);
  const [historicData, setHistoricData] = useState<any>(null);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const allCollectorsData = useMemo(() => [
    ...collectorRecords,
    ...(DeActivatedCollectorsFromApi || []).map((collector: any) => ({
      ...collector,
      isDeactivated: true,
    })),
  ].sort((a, b) => {
    if (a.disableDate === null && b.disableDate !== null) {
      return -1;
    }
    if (a.disableDate !== null && b.disableDate === null) {
      return 1;
    }
    return 0;
  }), [collectorRecords, DeActivatedCollectorsFromApi]);

  const isPending = isUpdateCollectorPending || isDeleteCollectorPending || isDeActivatedCollectorsFetching || isSiteUpdating;

  const generatedCollectorsColumns: MRT_ColumnDef<any>[] = useMemo(() => collectorsColumns(
    siteData || ({} as SiteByIdDTO),
    updateCollectorRequest,
    notify,
    setIsEditUnitCost,
    isEditUnitCost,
    setShowSideInfoDrawer,
    setUnitCostData,
    UnitCostData,
    setOpenSnackBar,
    openSnackBar,
    setSiteData,
    setIsSiteUpdating,
  ), [
    siteData,
    isEditUnitCost,
    UnitCostData,
    openSnackBar,
    // These are stable but included for completeness
    updateCollectorRequest,
    notify,
    setIsEditUnitCost,
    setShowSideInfoDrawer,
    setUnitCostData,
    setOpenSnackBar,
    setSiteData,
    setIsSiteUpdating,
  ]);

  const handleCloseCollectorModal = () => {
    setCollectorModalOpen(false);
    setEditCollector(undefined);
    setIsNotesOnlyMode(false);
  };

  const handleEditCollector = (rowData: any, type: string) => {
    setClickType(type);
    setEditCollector(rowData);
    setIsNotesOnlyMode(false);
    setCollectorModalOpen(true);
  };

  const handleEditNotesInCollector = (rowData: any) => {
    setEditCollector(rowData);
    setIsNotesOnlyMode(true);
    setCollectorModalOpen(true);
  };

  const handleREC = (collector: any, currentSiteId: any) => {
    if (collector && collector.id && currentSiteId) {
      navigate(`/rec/${collector.id}?s=${currentSiteId}`, { state: { collector } });
    } else {
      notify.error("Some error has occurred while opening selected Utility Provider Renewable Energy Rate");
    }
  };

  const handleUtilitySpecificEmissionsFactors = (collectorId: any, currentSiteId: any) => {
    navigate(`/utility-specific-emissions-factors/${collectorId}?s=${currentSiteId}`);
  };

  const showRecIcon = (row: any) => {
    if (row?.recsDetail === null || !row?.recs) {
      return <EsgRecIcon color="grey" />;
    }

    return <EsgRecIcon color={row?.recsDetail?.recsUploaded ? "green" : "red"} />;
  };

  const showEmissionFactorIcon = (row: any) => {
    const isDocumentUploaded = row?.emissionFactorDetails?.some((t: any) => t.documentUploaded === true)
    if (row?.emissionFactorDetails?.length === 0) {
      return <EsgTruck color="grey" />;
    }

    return <EsgTruck color={isDocumentUploaded ? COLORS.Mainblue : "red"} />;
  };

  const handleDeleteCollector = async (collector: any) => {
    const collectorId = collector.id;
    if (siteData?.id && collectorId) {
      try {
        await deleteCollectorRequest(collectorId, {
          onSuccess: () => {
            notify.success("Collector successfully deleted!");
            setIsDeleteDialogOpen(false);
            queryClient.invalidateQueries([QUERY_KEYS.SITES]);
            queryClient.invalidateQueries([
              QUERY_KEYS.COLLECTORS,
              QUERY_KEYS.SITES,
              site?.id,
            ]);
            queryClient.invalidateQueries([
              QUERY_KEYS.COLLECTORS,
              'deactivated',
              QUERY_KEYS.SITES,
              site?.id,
            ]);
            if (site) {
              setIsSiteUpdating(true);
              getSiteById(site?.id)
                .then((siteDetails) => {
                  setSiteData(siteDetails);
                })
                .catch((error) => {
                  // eslint-disable-next-line
                  console.error("Error fetching site data:", error);
                })
                .finally(() => {
                  setIsSiteUpdating(false);
                });
            }
          },
        })
      } catch (error: any) {
        notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : "This collector has associated bills. Please remove the bills first");
        setIsDeleteDialogOpen(false);
      }
    }
  };

  const handlerOpenCollectorDeleteModal = async (collector: any) => {
    setCurrentCollectorToDelete(collector);
    setIsDeleteDialogOpen(true);
  };

  useEffect(() => {
    if (siteData?.id) {
      refetchCollectors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteData?.id]);

  useEffect(() => {
    const fetchData = async () => {
      if (UnitCostData) {
        try {
          const data = await getUnitCostByCollectorId(UnitCostData?.id);
          setHistoricData(data);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };

    fetchData();
  }, [UnitCostData]);

  useEffect(() => {
    if (tableContainerRef.current) {
      const container = tableContainerRef.current;
      container.scrollTo({
        left: showSideInfoDrawer ? container.scrollWidth : 0,
        behavior: "smooth",
      });
    }
  }, [showSideInfoDrawer]);

  return (
    <Box
      sx={{
        px: 3,
        mt: 1,
        pr: showSideInfoDrawer ? "410px" : 3,
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <p style={{ fontSize: "16px", fontWeight: "500" }}>
          Utility Providers
        </p>
        <Button
          sx={{ borderRadius: "8px", width: "200px", height: "36px" }}
          variant="contained"
          onClick={() => {
            setEditCollector(undefined);
            setIsNotesOnlyMode(false);
            setCollectorModalOpen(true);
          }}
          startIcon={<PlusIcon />}
          disabled={userRole.isAuditor}
        >
          Add Utility Provider
        </Button>
      </Box>
      <Box>
        <CustomMaterialTable
          data={allCollectorsData}
          columns={generatedCollectorsColumns}
          state={{ isLoading: isPending }}
          enablePagination={false}
          enableTopToolbar={false}
          enableRowVirtualization={false}
          rowClickable
          enableRowActions
          renderRowActions={({ row }) => (
            <div
              style={{
                color: row.original.isDeactivated ? "red" : "",
                height: "45px",
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
              }}
            >
              <Box
                sx={{
                  height: "45px",
                  display: "flex",
                  alignItems: "center",
                  px: "5px",
                }}
              >
                <SwitchToActivation
                  enabled={row.original.enabled}
                  textOn="Deactivate"
                  textOff="Activate"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (site?.enabled) {
                      handleEditCollector(row.original, row.original?.enabled ? "Disable" : "Enable")
                    } else {
                      notify.error("You cannot reactivate a collector belonging to a deactivated site")
                    }
                  }}
                />
                <Tooltip arrow placement="top" title="Notes">
                  <IconButton
                    sx={{ width: "36px", height: "36px", padding: "4px" }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditNotesInCollector(row.original)
                    }}
                  >
                    <EsgNotesIcon color={row.original.notes?.length > 0 ? COLORS.Romance : 'grey'} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  arrow
                  placement="top"
                  title={row?.original?.recsDetail === null
                    ? 'Upload Utility Provider Renewable Energy Rate'
                    : row?.original?.recsDetail?.recsUploaded === true
                      ? 'Utility Provider Renewable Energy Rate is Uploaded'
                      : 'Utility Provider Renewable Energy Rate document missing'}
                >
                  <IconButton
                    sx={{ width: "36px", height: "36px", padding: "4px" }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleREC(row.original, siteData?.id)
                    }}
                  >
                    {showRecIcon(row?.original)}
                  </IconButton>
                </Tooltip>
                {/* Utility Specific Emissions Factors */}
                <Tooltip
                  arrow
                  placement="top"
                  title="Utility Provider-specific EF"
                >
                  <IconButton
                    sx={{ width: "36px", height: "36px", padding: "4px" }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUtilitySpecificEmissionsFactors(row.original?.id, siteData?.id)
                    }}
                  >
                    {showEmissionFactorIcon(row?.original)}
                  </IconButton>
                </Tooltip>
                {/* Delete collector (Only Super Admin) or deactivate collector */}
                {isSuperAdmin
                  ? <Tooltip arrow placement="top" title="Delete">
                    <IconButton
                      sx={{ width: "36px", height: "36px", padding: "4px" }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handlerOpenCollectorDeleteModal(row.original)
                      }}
                    >
                      <EsgDeleteIcon color="#EF2E14" />
                    </IconButton>
                  </Tooltip>
                  : null}
              </Box>
            </div >
          )}
          muiTableContainerProps={{
            sx: {
              width: "calc(100vw - 150px)",
              "& tbody tr td": {
                paddingY: "0px",
              },
              "& thead tr th": {
                paddingY: "12px",
              },
              "& thead tr th:first-of-type": {
                borderRight: "1px solid #e0e0e0",
                "& > div": { pl: "8px" },
              },
              "& tbody tr td:first-of-type": {
                borderRight: "1px solid #e0e0e0",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => handleEditCollector(row.original, ""),
          })}
        />
      </Box>
      {isCollectorModalOpen && <CollectorModal
        isOpen={isCollectorModalOpen}
        clickType={clickType}
        onClose={handleCloseCollectorModal}
        allowedValues={allowedValues}
        site={siteData ? { ...siteData, name: siteData.name || "" } : undefined}
        editCollector={editCollector}
        isNotesOnlyMode={isNotesOnlyMode}
        onCollectorDataUpdate={onCollectorDataUpdate}
      />}
      {isDeleteDialogOpen && <DeleteDialog
        isOpen={isDeleteDialogOpen}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onDeleteClick={() => handleDeleteCollector(currentCollectorToDelete)}
        target="Delete Collector"
        title="Delete Utility Provider?"
      />}
      {showSideInfoDrawer && (
        <UnitCostInfoDrawer
          unitCostData={UnitCostData}
          historicData={historicData}
          isOpen={showSideInfoDrawer}
          onClose={() => setShowSideInfoDrawer(false)}
        />
      )}
    </Box>
  )
};
